/* src/contexts/GradesContext.jsx */
import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import {
  DEFAULT_CORE_DATA,
  readCalculatorValue,
} from "../utils/calculatorStorage";

const GradesStateContext = createContext();
const GradeActionsContext = createContext();

const EMPTY_GRADE = { grade: null, subject: null, level: null, neededText: null };

export function GradesProvider({ children }) {
  const [grades, setGrades] = useState(() => readCalculatorValue("grades", {})); // { groupId: { grade, subject, level } }

  const updateGrade = useCallback((groupId, grade, subject, level, neededText = null) => {
    setGrades((prev) => {
      const current = prev[groupId] || EMPTY_GRADE;

      if (
        current.grade === grade &&
        current.subject === subject &&
        current.level === level &&
        current.neededText === neededText
      ) {
        return prev;
      }

      return {
        ...prev,
        [groupId]: { grade, subject, level, neededText },
      };
    });
  }, []);

  // Special state for Group 7 (TOK + EE)
  const [coreData, setCoreData] = useState(() =>
    readCalculatorValue("coreData", DEFAULT_CORE_DATA)
  );

  const updateCoreData = useCallback((corePoints, tokGrade, eeGrade) => {
    setCoreData((prev) => {
      if (
        prev.corePoints === corePoints &&
        prev.tokGrade === tokGrade &&
        prev.eeGrade === eeGrade
      ) {
        return prev;
      }

      return { corePoints, tokGrade, eeGrade };
    });
  }, []);

  const resetGrades = useCallback(() => {
    setGrades({});
    setCoreData(DEFAULT_CORE_DATA);
  }, []);

  const stateValue = useMemo(
    () => ({ grades, coreData }),
    [grades, coreData]
  );
  const actionsValue = useMemo(
    () => ({ resetGrades, updateGrade, updateCoreData }),
    [resetGrades, updateGrade, updateCoreData]
  );

  return (
    <GradesStateContext.Provider value={stateValue}>
      <GradeActionsContext.Provider value={actionsValue}>
        {children}
      </GradeActionsContext.Provider>
    </GradesStateContext.Provider>
  );
}

export function useGradesState() {
  return useContext(GradesStateContext);
}

export function useGradeActions() {
  return useContext(GradeActionsContext);
}

export function useGrades() {
  return {
    ...useGradesState(),
    ...useGradeActions(),
  };
}
